import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Page,
  Card,
  PageActions,
  Text,
  Loading,
  FormLayout,
  TextField,
  Toast,
  Modal,
  Select,
  Checkbox,
  Thumbnail,
  DropZone,
  InlineError,
  InlineStack,
  Button,
} from "@shopify/polaris";
import axios from "../../../Assets/Lib/axios";
import { useParams, useNavigate } from "react-router-dom";
import LoadingScreen from "../../../Components/LoadingScreen";

const AddEditScreenshot = ({ mode, url }) => {
  const navigate = useNavigate();
  const { id, screenshotId } = useParams();
  const [isSaving, setIsSaving] = useState(mode === "edit");

  const [item, setItem] = useState({
    image: "",
  });

  const [errors, setErrors] = useState({
    name: "",
  });

  const handleChange = useCallback((field, value) => {
    setItem((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  }, []);

  const [active, setActive] = useState(false);
  const toggleActive = useCallback(() => setActive((a) => !a), []);
  const toastMarkup = active ? (
    <Toast
      content="Changes have been saved successfully"
      onDismiss={toggleActive}
    />
  ) : null;

  const [activeDelete, setActiveDelete] = useState(false);
  const toggleDeleteModal = useCallback(() => {
    setActiveDelete((a) => !a);
    setIsSaving(false);
  }, []);

  const handleYesDelete = () => {
    setIsSaving(true);
    axios
      .delete(`/competitors/${id}/gallery/${screenshotId}`)
      .then(() => {
        navigate(`/admin/competitors/${id}/screenshots`);
      })
      .catch(console.error);
  };

  const validateFields = () => {
    let hasError = false;
    const newErrors = {};

    if (!item.name.trim()) {
      newErrors.name = "This field is required";
      hasError = true;
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return !hasError;
  };

  const [isImageUploading, setIsImageUploading] = useState(false);
  const validImageTypes = [
    "image/gif",
    "image/jpeg",
    "image/png",
    "image/webp",
  ];
  const [files, setFiles] = useState();
  const handleDropZone = useCallback(
    (_dropFiles, acceptedFiles, _rejectedFiles) => setFiles(acceptedFiles[0]),
    []
  );

  const fileUpload = !files && <DropZone.FileUpload />;
  const uploadedFiles = files && (
    <InlineStack gap="200" align="start" blockAlign="center">
      <div className="maxtextwidth">
        {files.name}{" "}
        <Text variant="bodySm" as="p">
          {files.size} bytes
        </Text>
      </div>
    </InlineStack>
  );
  const [imageError, setImageError] = useState("");
  const imageKey = useRef("");

  const sendImage = (files) => {
    setIsImageUploading(true);
    const form_data = new FormData();
    form_data.append("entity_type", "competitor");
    form_data.append("image", files[files.length - 1]);

    axios
      .post(`/s3/save-file`, form_data)
      .then((result) => {
        imageKey.current = result.data.data.file_key;
        setIsImageUploading(false);
        setItem({
          ...item,
          image:
            validImageTypes.indexOf(files[files.length - 1].type) > -1
              ? window.URL.createObjectURL(files[files.length - 1])
              : "https://cdn.shopify.com/s/files/1/0757/9955/files/New_Post.png",
        });
        setImageError("");
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (mode === "edit") {
      axios
        .get(`/competitors/${id}/gallery/${screenshotId}`)
        .then((result) => {
          const data = result?.data?.data || {};
          setItem({
            image: data?.url || "-",
            // is_active: data?.is_active ? true : false,
          });
          setIsSaving(false);
        })
        .catch((err) => console.log(err));
    }
  }, []);
  const [error, setError] = useState("");
  return (
    <Page
      title={`${mode === "new" ? "Add" : "Edit"} Screenshot`}
      backAction={{
        onAction: () => navigate(`/admin/competitors/${id}/screenshots`),
      }}
    >
      {isSaving && <Loading />}

      <Card sectioned>
        <FormLayout>
          <Text as="p" fontWeight="regular">
            Image
          </Text>
          <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
            <div style={{ float: "left" }}>
              <Thumbnail size="large" source={item.image ? item.image : ""} />
            </div>
            <div style={{ width: "100%", marginLeft: "10px" }}>
              <DropZone
                onDrop={handleDropZone}
                onDropAccepted={sendImage}
                allowMultiple={false}
              >
                {uploadedFiles}
                {fileUpload}
              </DropZone>
            </div>
          </div>
          <InlineError message={imageError} />

          {/* <Checkbox
            label="Is active"
            checked={item.is_active}
            onChange={(value) => handleChange("is_active", value)}
          /> */}
          <InlineError message={error} />
        </FormLayout>
      </Card>

      <PageActions
        primaryAction={{
          content: "Save",
          onAction: handleSave,
          loading: isSaving,
        }}
        secondaryActions={
          mode === "edit" && (
            <Button
              variant="primary"
              loading={isSaving}
              tone="critical"
              onClick={() => setActiveDelete(true)}
            >
              Delete
            </Button>
          )
        }
      />

      <Modal
        open={activeDelete}
        onClose={toggleDeleteModal}
        title="Delete Item"
        primaryAction={{
          content: "Yes",
          onAction: handleYesDelete,
          loading: isSaving,
        }}
        secondaryActions={[
          {
            content: "No",
            onAction: toggleDeleteModal,
            loading: isSaving,
          },
        ]}
      >
        <Modal.Section>
          <Text>Are you sure you want to delete this item?</Text>
        </Modal.Section>
      </Modal>

      {toastMarkup}
      {isImageUploading ? <LoadingScreen /> : null}
    </Page>
  );

  function handleSave() {
    // if (!validateFields()) {
    //   return;
    // }
    setIsSaving(true);
    const formData = new FormData();
    mode === "edit" && formData.append("_method", "PATCH");

    imageKey.current && formData.append("image", imageKey.current);
    // formData.append("is_active", item.is_active ? 1 : 0);

    axios
      .post(
        mode === "new"
          ? `/competitors/${id}/gallery`
          : `/competitors/${id}/gallery/${screenshotId}`,
        formData
      )
      .then(() => {
        mode === "new"
          ? navigate(`/admin/competitors/${id}/screenshots`)
          : toggleActive();
      })
      .catch((error) => {
        console.log(error.response);
        setIsSaving(false);
        if (error.response && error.response.data?.message) {
          console.log("error=", error.response.data?.message);
          const serverErrors = error.response.data.message;
          setError(serverErrors);
        } else {
          console.error(error);
        }
      })
      .finally(() => setIsSaving(false));
  }
};

export default AddEditScreenshot;
